import { ProductListQuery } from "@dashboard/graphql";
import { RelayToFlat } from "@dashboard/types";

type ProductNode = NonNullable<RelayToFlat<ProductListQuery["products"]>>[number];

/**
 * Lowercase + strip diacritics so "abracadeira" matches "Abraçadeira".
 * Mirrors `normalizeForSearch` in the maxcei-redparts-react-template storefront.
 */
export function normalizeForSearch(value: string): string {
  return (value || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}

/**
 * Best-effort plaintext extraction from Saleor's EditorJS JSON description.
 * Falls back to the raw string (tags stripped) when it isn't parseable JSON.
 */
function getDescriptionText(description?: string | null): string {
  if (!description) {
    return "";
  }

  try {
    const parsed = JSON.parse(description);

    if (parsed?.blocks && Array.isArray(parsed.blocks)) {
      const parts: string[] = [];

      parsed.blocks.forEach((block: { data?: { text?: string; items?: string[] } }) => {
        const data = block?.data ?? {};

        if (typeof data.text === "string") {
          parts.push(data.text);
        }

        if (Array.isArray(data.items)) {
          data.items.forEach(item => {
            if (typeof item === "string") {
              parts.push(item);
            }
          });
        }
      });

      return parts.join(" ").replace(/<[^>]*>/g, " ");
    }
  } catch {
    // Not JSON — fall through to raw handling below.
  }

  return description.replace(/<[^>]*>/g, " ");
}

/** Build the normalized haystack of name + variant SKUs + description. */
function getSearchHaystack(node: ProductNode): string {
  const skus = (node.variants ?? []).map(variant => variant?.sku).filter(Boolean) as string[];

  return normalizeForSearch(
    [node.name, ...skus, getDescriptionText(node.description)].filter(Boolean).join(" "),
  );
}

/** Every whitespace-separated term must appear in the product's searchable text. */
export function productMatchesSearch(node: ProductNode, terms: string[]): boolean {
  if (terms.length === 0) {
    return true;
  }

  const haystack = getSearchHaystack(node);

  return terms.every(term => haystack.includes(term));
}

/** Split a raw query into normalized search terms. */
export function getSearchTerms(query: string): string[] {
  return normalizeForSearch(query).split(/\s+/).filter(Boolean);
}

// Synthetic cursors for the in-memory paginated search results. They encode the
// page's start offset so the existing link-based `usePaginator` keeps working.
const CURSOR_PREFIX = "client-search:";

export function encodeOffsetCursor(offset: number): string {
  return btoa(`${CURSOR_PREFIX}${offset}`);
}

export function decodeOffsetCursor(cursor?: string | null): number | null {
  if (!cursor) {
    return null;
  }

  try {
    const decoded = atob(cursor);

    if (!decoded.startsWith(CURSOR_PREFIX)) {
      return null;
    }

    const offset = Number.parseInt(decoded.slice(CURSOR_PREFIX.length), 10);

    return Number.isNaN(offset) ? null : offset;
  } catch {
    return null;
  }
}
